import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import type { U3130ApprovalListPresetApi } from "./tools/u3130ApprovalListPreset";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import type {
  U3130AutoCompletCriterias,
  U3130FindAvailablePeriodsRequest,
  U3130FindTasksForProjectRequest,
} from "./U3130TimesheetModel";

export type { U3130InitLU, U3130Timesheet } from "./U3130TimesheetModel";
export { normalizeTimesheetFromApi } from "./U3130TimesheetModel";
export type { U3130ApprovalListPresetApi } from "./tools/u3130ApprovalListPreset";

/** Recherche liste U3130 : filtre générique + préréglage métier (le backend peut s’en servir seul ou avec les criterias). */
export interface U3130SearchPayload extends U2000SearchPayload {
  approvalListPreset?: U3130ApprovalListPresetApi;
}

const LOGICALUNIT_URL = "U3130";

/** Base UL saisie feuille de temps : autocomplete projet / sous-projet / tâches (lecture seule approbation). */
const U3130_TIMESHEET_ENTRY_BASE = "U3129";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  FINDEMPLOYEE: string;
  EXPORT_EXCEL: string;
  EXPORT_SOMMAIRE_PAIE_PDF: string;
  EXPORT_EMPLOYEUR_D: string;
  EXPORT_DEPENSES_PAR_SEMAINE_PAR_CODE_PDF: string;
  GET: (id: string | number) => string;
  APPROVE: (id: string | number) => string;
  APPROVE_BATCH: string;
  REFUSE: (id: string | number) => string;
  BEGIN_CORRECTION: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  EXPORT_SOMMAIRE_PAIE_PDF: `${LOGICALUNIT_URL}/exportSommairePaiePdf`,
  EXPORT_EMPLOYEUR_D: `${LOGICALUNIT_URL}/exportEmployeurD`,
  EXPORT_DEPENSES_PAR_SEMAINE_PAR_CODE_PDF: `${LOGICALUNIT_URL}/exportDepensesParSemaineParCodePdf`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  APPROVE: (id: string | number) => `${LOGICALUNIT_URL}/approve/${id}`,
  APPROVE_BATCH: `${LOGICALUNIT_URL}/approveBatch`,
  REFUSE: (id: string | number) => `${LOGICALUNIT_URL}/refuse/${id}`,
  BEGIN_CORRECTION: (id: string | number) => `${LOGICALUNIT_URL}/beginCorrection/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

const TimesheetEntryEndpoints = {
  FINDPROJECT: `${U3130_TIMESHEET_ENTRY_BASE}/findProject`,
  FINDSUBPROJECT: `${U3130_TIMESHEET_ENTRY_BASE}/findSubproject`,
  FINDTASKSFORPROJECT: `${U3130_TIMESHEET_ENTRY_BASE}/findTasksForProject`,
  FINDAVAILABLEPERIODS: `${U3130_TIMESHEET_ENTRY_BASE}/findAvailablePeriods`,
} as const;

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, payload: U3130SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, payload);
};

/** Export Excel liste (filtre courant + préréglage approbation) — même schéma async que les autres UL (`processId` / `id`). */
export const doExportExcel = (
  axios: AxiosInstance,
  filter: U2000SearchParameter,
  lang: "fr" | "en" | undefined,
  approvalListPreset: U3130ApprovalListPresetApi,
) => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang, approvalListPreset });
};

/** Corps POST exportSommairePaiePdf / exportEmployeurD — job async U2200. */
export interface U3130PayrollPeriodReportExportRequest {
  lang?: "fr" | "en";
  startDate: string;
  endDate: string;
  /** Libellé période paie (combo rapport) — sous-titre PDF ou contexte export. */
  payrollPeriodLabel?: string;
}

export type U3130ExportSommairePaiePdfRequest = U3130PayrollPeriodReportExportRequest;

export type U3130ExportEmployeurDRequest = U3130PayrollPeriodReportExportRequest;

/** Lance la génération asynchrone du PDF sommaire paie (201 + job). */
export const doExportSommairePaiePdf = (axios: AxiosInstance, body: U3130ExportSommairePaiePdfRequest) => {
  return axios.post(LogicalUnitEndpoints.EXPORT_SOMMAIRE_PAIE_PDF, body);
};

/** Lance la génération asynchrone de l’export Excel Employeur D (201 + job U2200). */
export const doExportEmployeurD = (axios: AxiosInstance, body: U3130ExportEmployeurDRequest) => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EMPLOYEUR_D, body);
};

/** Corps POST exportDepensesParSemaineParCodePdf — job async, PDF via centre de notifications (même mécanique que le sommaire paie). */
export interface U3130ExportDepensesParSemaineParCodePdfRequest {
  lang?: "fr" | "en";
  startDate: string;
  endDate: string;
  /** Préfixe du sous-titre du PDF (ex. libellé période de paie). */
  payrollPeriodLabel?: string;
}

/** Lance la génération asynchrone du PDF dépenses par semaine par code (201 + job U2200). */
export const doExportDepensesParSemaineParCodePdf = (axios: AxiosInstance, body: U3130ExportDepensesParSemaineParCodePdfRequest) => {
  return axios.post(LogicalUnitEndpoints.EXPORT_DEPENSES_PAR_SEMAINE_PAR_CODE_PDF, body);
};

export const doGet = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

/** Corps POST approve — champs optionnels selon `employee.remunerationType` (Salary / Hourly). */
export interface U3130ApproveRequestBody {
  id: string;
  version: number;
  /** Salary : total travaillé (souvent = somme feuille). */
  totalHours?: number;
  totalPaidHours?: number;
  unpaidHours?: number;
  /** Hourly : dépôt banque d’heures spéciale. */
  regularHoursToBank?: number;
  halfTimeHoursToBank?: number;
  doubleTimeHoursToBank?: number;
}

export const doApprove = (axios: AxiosInstance, id: string | number, body: U3130ApproveRequestBody) => {
  return axios.post(LogicalUnitEndpoints.APPROVE(id), body);
};

/** Ligne résumé POST /U3130/approveBatch (BL-0640). */
export interface U3130ApproveBatchLine {
  id: string;
  periodDate?: string;
  codeEmployee?: string;
  reason?: string;
  message?: string;
}

export interface U3130ApproveBatchResult {
  approvedCount: number;
  skippedCount: number;
  errorCount: number;
  approved: U3130ApproveBatchLine[];
  skipped: U3130ApproveBatchLine[];
  errors: U3130ApproveBatchLine[];
}

export interface U3130ApproveBatchBody {
  filter: U2000SearchParameter;
  dryRun?: boolean;
}

export const doApproveBatch = async (axios: AxiosInstance, body: U3130ApproveBatchBody): Promise<U3130ApproveBatchResult> => {
  const res = await axios.post(LogicalUnitEndpoints.APPROVE_BATCH, body);
  const raw = res.data?.result ?? res.data;
  return raw as U3130ApproveBatchResult;
};

export const doRefuse = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post(LogicalUnitEndpoints.REFUSE(id), body);
};

export const doBeginCorrection = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post(LogicalUnitEndpoints.BEGIN_CORRECTION(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  return axios.get(LogicalUnitEndpoints.AUDITS(id), {
    params: { b: U2000GenerateUrlUUID() },
  });
};

export const doFindProjectForDisplay = (axios: AxiosInstance, criterias: U3130AutoCompletCriterias) => {
  return axios.post(TimesheetEntryEndpoints.FINDPROJECT, criterias);
};

export const doFindSubprojectForDisplay = (axios: AxiosInstance, criterias: U3130AutoCompletCriterias) => {
  return axios.post(TimesheetEntryEndpoints.FINDSUBPROJECT, criterias);
};

export const doFindTasksForProjectForDisplay = (axios: AxiosInstance, body: U3130FindTasksForProjectRequest) => {
  return axios.post(TimesheetEntryEndpoints.FINDTASKSFORPROJECT, body);
};

export const doFindAvailablePeriodsForDisplay = (axios: AxiosInstance, body: U3130FindAvailablePeriodsRequest) => {
  return axios.post(TimesheetEntryEndpoints.FINDAVAILABLEPERIODS, body);
};

export const doFindEmployeeForDisplay = (axios: AxiosInstance, criterias: U3130AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias);
};

export function luHandleApiError(t: TranslateFn, NS: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; errorType?: string; description?: string };

  if (apiErr?.errorCode === "B0014" || apiErr?.errorType === "BankBalanceExceeded") {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("msgBankHoursExceedsBalance", {
        ns: NS,
        defaultValue: "Le solde d'une des banques d'heures a été dépassé.",
      }),
    });
  }
}
